<template>
    <div id="map" ref="mapElementRef" class="map">
        <div class="map-controls">
            <!-- Componentes de controle do canto inferior esquerdo devem ser colocados dentro desta div -->
            <GeneralMapControls :map="mapRef" v-if="!!mapRef" />
        </div>

        <div class="map-layer-switch">
            <MapLayerSwitcher :map="mapRef" default-base-map="g_sat" v-if="!!mapRef" />
        </div>
    </div>
</template>

<script setup>
import GeneralMapControls from "../MapsOpenLayers/Components/GeneralMapControls.vue";
import MapLayerSwitcher from "../MapsOpenLayers/Components/MapLayerSwitcher.vue";
import {defineProps, onMounted, ref} from "vue";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import {Vector} from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import {bbox} from "ol/loadingstrategy";
import Text from "ol/style/Text";
import Map from "ol/Map";
import {defaults, ScaleLine} from "ol/control";
import View from "ol/View";
import {DragRotate} from "ol/interaction";
import {geoJsonReadOptions} from "../../../open-layers/projections";
import {createEmpty, extend} from "ol/extent";
import {baseLayers} from "../../../open-layers/baseLayers";
import {getDefaultMapView} from "../../../open-layers/map-defaults";

const props = defineProps(['posic', 'zoom_busca', 'geom_lote', 'geom_edif', 'geom_ruas', 'geom_conf', 'geom_quadra'])
const mapElementRef = ref(null)
const mapRef = ref(null)

const z = props.zoom_busca
const geoJsonFormat = new GeoJSON()
const readProjectedFeatures = (features) => geoJsonFormat.readFeatures(features, geoJsonReadOptions())

const geojsonObject = {
    'type': 'FeatureCollection',
    'features': JSON.parse(props.geom_lote)
};

const vectorSource = new Vector({features: readProjectedFeatures(geojsonObject)});
const vectorLayer = new VectorLayer({
    source: vectorSource,
    title: 'Lotes',
    geo_table: 'public.geo_lote',
    displayInLayerSwitcher: true,
    strategy: bbox,
    visible: true,
    style: function (f) {
        return new Style({
            text: new Text({
                text: f.get('medida'),
                font: 'bold 11px sans-serif',
                stroke: new Stroke({width: 3, color: [255, 255, 255]}),
            }),
            stroke: new Stroke({width: 4, color: f.get('color') || [255, 128, 0]}),
            fill: new Fill({color: (f.get('color') || [255, 128, 0]).concat([.3])})
        })
    }
});

const geojsonObject_edif = {
    'type': 'FeatureCollection',
    'features': JSON.parse(props.geom_edif)
};

const vectorSource_edif = new Vector({features: readProjectedFeatures(geojsonObject_edif)});
const vectorLayer_edif = new VectorLayer({
    source: vectorSource_edif,
    title: 'Edificação',
    geo_table: 'geo_edificacao',
    displayInLayerSwitcher: true,
    strategy: bbox,
    visible: true,
    style: function (f) {
        return new Style({
            text: new Text({
                text: f.get('medida'),
                font: 'bold 11px sans-serif',
                stroke: new Stroke({width: 2.5, color: [255, 255, 255]}),
            }),
            stroke: new Stroke({width: 5.5, color: f.get('color') || [0, 255, 255]}),
            fill: new Fill({color: (f.get('color') || [0, 0, 255]).concat([.3])})
        })
    }
});

const geojsonObjectConf = {
    'type': 'FeatureCollection',
    'features': JSON.parse(props.geom_conf)
};

const vectorSourceConf = new Vector({features: readProjectedFeatures(geojsonObjectConf)});
const vectorLayerConf = new VectorLayer({
    source: vectorSourceConf,
    title: 'Lotes Confrontantes',
    geo_table: 'public.geo_lote',
    displayInLayerSwitcher: true,
    strategy: bbox,
    visible: true,
    style: function (f) {
        return new Style({
            text: new Text({
                text: f.get('matricula'),
                font: 'bold 11px sans-serif',
                stroke: new Stroke({width: 1, color: [255, 255, 255]}),
            }),
            stroke: new Stroke({width: 4, color: f.get('color') || [255, 0, 0]}),
            fill: new Fill({color: (f.get('color') || [255, 128, 0]).concat([.3])})
        })
    }
});

const geojsonObjectQuadra = {
    'type': 'FeatureCollection',
    'features': JSON.parse(props.geom_quadra)
};

const vectorSourceQuadra = new Vector({features: readProjectedFeatures(geojsonObjectQuadra)});
const vectorLayerQuadra = new VectorLayer({
    source: vectorSourceQuadra,
    title: 'Quadra',
    geo_table: 'public.geo_quadras',
    displayInLayerSwitcher: true,
    strategy: bbox,
    visible: true,
    style: function (f) {
        return new Style({
            text: new Text({
                text: f.get('codigo'),
                font: 'bold 11px sans-serif',
                stroke: new Stroke({width: 2, color: [255, 255, 255]}),
            }),
            stroke: new Stroke({width: 3, color: f.get('color') || [255, 140, 0]}),
            fill: new Fill({color: (f.get('color') || [255, 140, 0]).concat([.06])})
        })
    }
});

const geojsonObject_ruas = {
    'type': 'FeatureCollection',
    'features': JSON.parse(props.geom_ruas)
};

const vectorSource_ruas = new Vector({features: readProjectedFeatures(geojsonObject_ruas)});
const vectorLayer_ruas = new VectorLayer({
    source: vectorSource_ruas,
    title: 'Ruas',
    geo_table: 'geo_ruas',
    displayInLayerSwitcher: true,
    strategy: bbox,
    visible: true,
    style: function (f) {
        const baseColor = f.get('color') || [255, 0, 0];
        return new Style({
            text: new Text({
                text: f.get('medida'),
                font: 'bold 11px sans-serif',
                placement: 'line',
                repeat: 200,
                textAlign: 'center',
                textBaseline: 'bottom',
                fill: new Fill({ color: '#000' }),
                stroke: new Stroke({ width: 3.5, color: [255, 255, 255] }),
            }),
            stroke: new Stroke({
                width: 3.5,
                color: baseColor
            }),
            fill: new Fill({
                color: [...baseColor, 0.3]
            })
        });
    }
});

const vectorSources = [vectorSource, vectorSource_edif, vectorSourceConf, vectorSourceQuadra, vectorSource_ruas]

onMounted(() => {
    const map = new Map({
        layers: [
            baseLayers('g_sat'),
            vectorLayerQuadra,
            vectorLayer,
            vectorLayer_edif,
            vectorLayerConf,
            vectorLayer_ruas,
        ],
        target: 'map',
        controls: defaults({
            attribution: false,
            attributionOptions: ({collapsible: false,}),
            zoom: false,
            rotate: false,
            fullscreen: false
        }),
        view: new View(getDefaultMapView())
    });

    const combinedExtent = createEmpty();
    vectorSources.forEach((source) => {
        const sourceExtent = source.getExtent();
        if (sourceExtent && sourceExtent.every(Number.isFinite)) {
            extend(combinedExtent, sourceExtent);
        }
    });

    map.getView().fit(combinedExtent, {
        padding: [60, 60, 60, 60],
        maxZoom: z,
    });
    window.addEventListener('resize', () => (!!map) ? map.updateSize() : '');

    mapElementRef.value.setAttribute('tabindex', '0')
    mapElementRef.value.addEventListener('mouseenter', function () {
        this.focus()
    });
    mapElementRef.value.addEventListener('mouseleave', function () {
        this.blur()
    });

    const dragRotateInteraction = new DragRotate({
        condition: function (mapBrowserEvent) {
            const isAltPressed = mapBrowserEvent.originalEvent.altKey;
            const isShiftPressed = mapBrowserEvent.originalEvent.shiftKey;
            return isAltPressed && isShiftPressed;
        }
    });
    map.addInteraction(dragRotateInteraction);

    map.getViewport().addEventListener('pointerdown', function (event) {
        if (event.altKey || event.shiftKey) {
            event.preventDefault();
            event.stopPropagation();
        }
    });

    map.getViewport().addEventListener('keydown', function(event) {
        if (event.altKey && event.shiftKey) {
            event.preventDefault();
            event.stopPropagation();
        }
    });

    map.addControl(new ScaleLine());
    mapRef.value = map;
})
</script>

<style scoped>
html, body {
    margin: 0;
    padding: 0;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

#map {
    width: 100%;
    height: 420px;
    position: relative;
    background-color: white;
}

.panel-measure {
    position: absolute;
    top: 10px; /* Mantém a 10px do topo */
    right: 10px; /* ALTERADO: Posiciona a 10px da direita */
    left: auto; /* Adicionado: Remove o 'left' explícito para não haver conflito */
    background-color: rgba(255, 255, 255, 0.7);
    padding: 3px;
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    display: flex;
    flex-direction: row; /* ALTERADO: para colocar os itens lado a lado */
    gap: 5px; /* Mantém o espaçamento entre os botões */
}

.panel-measure button {
    padding: 4px 6px;
    border: 1px solid #fff;
    background-color: rgba(0, 60, 136, .6);
    cursor: pointer;
    border-radius: 3px;
    font-size: 14px;
    text-align: center;

    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;
}

.panel-measure button:hover {
    background-color: #ddd;
}

.panel-measure button.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.8;
    white-space: nowrap;
    font-size: 12px;
    cursor: default;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: #ffcc33;
    color: black;
    border: 1px solid white;
    opacity: 1;
}

.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top-color: rgba(0, 0, 0, 0.5);
}

.ol-zoom {
    right: 0.5em;
    left: auto;
    top: 0.5em;
}

:deep(.ol-scale-line) {
    left: auto !important;
    right: 8px !important;
    bottom: 20px !important;
}

.north-arrow {
    position: absolute;
    top: 40px;
    right: 20px;
    width: 120px;
    height: 120px;
    background-size: contain;
    background-repeat: no-repeat;
    z-index: 999;
    transform-origin: center center;
}

@media print {

    .panel-measure {
        display: none !important;
    }

    .ol-control, .ol-zoom, .ol-rotate {
        display: none !important;
    }

    .ol-tooltip, .ol-tooltip-measure {
        display: none !important;
    }

    #map {
        height: 100vh !important;
        width: 100% !important;
        box-shadow: none !important;
        background-color: white;
    }

    .ol-layerswitcher > button {
        background-color: rgba(255, 255, 245, 1) !important;
        border: 1px solid #fff;
        border-radius: 3px;
        color: black;
    }

    .ol-layerswitcher {
        right: 320px !important;
        top: 10px !important;
        left: auto !important;
        z-index: 1000;
    }
}

.map-controls {
    position: absolute;
    display: flex;
    flex-direction: column;
    gap: 8px;
    bottom: 20px;
    left: 8px;
    z-index: 1000;
}
</style>
